import { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  { value: 'acheteur', label: 'Acheteur' },
  { value: 'locataire', label: 'Locataire' },
  { value: 'investisseur', label: 'Investisseur' },
  { value: 'partenaire', label: 'Partenaire' },
  { value: 'architecte', label: 'Architecte' },
  { value: 'autre', label: 'Autre' },
];
const STATUSES = [
  { value: 'nouveau', label: 'Nouveau' },
  { value: 'prospect', label: 'Prospect' },
  { value: 'qualifie', label: 'Qualifié' },
  { value: 'client', label: 'Client' },
  { value: 'perdu', label: 'Perdu' },
];

const CrmContactForm = ({ existing = null, onClose, onSaved }) => {
  const isEdit = !!existing;
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', company: '',
    role: 'autre', status: 'nouveau', tags: '',
    notes: '',
    budget_min_eur: '', budget_max_eur: '',
    surface_min_m2: '', surface_max_m2: '',
    desired_typology: '',
    consent_marketing: false,
    consent_data_processing: false,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (existing) {
      setForm({
        first_name: existing.first_name || '',
        last_name: existing.last_name || '',
        email: existing.email || '',
        phone: existing.phone || '',
        company: existing.company || '',
        role: existing.role || 'autre',
        status: existing.status || 'nouveau',
        tags: (existing.tags || []).join(', '),
        notes: existing.notes || '',
        budget_min_eur: existing.budget_min_eur || '',
        budget_max_eur: existing.budget_max_eur || '',
        surface_min_m2: existing.surface_min_m2 || '',
        surface_max_m2: existing.surface_max_m2 || '',
        desired_typology: existing.desired_typology || '',
        consent_marketing: !!existing.consent_marketing,
        consent_data_processing: !!existing.consent_data_processing,
      });
    }
  }, [existing]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!form.first_name.trim()) return toast.error('Prénom requis');
    setBusy(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        company: form.company.trim() || null,
        role: form.role,
        status: form.status,
        tags: form.tags.split(',').map(s => s.trim()).filter(Boolean),
        notes: form.notes.trim() || null,
        budget_min_eur: form.budget_min_eur ? parseFloat(form.budget_min_eur) : null,
        budget_max_eur: form.budget_max_eur ? parseFloat(form.budget_max_eur) : null,
        surface_min_m2: form.surface_min_m2 ? parseFloat(form.surface_min_m2) : null,
        surface_max_m2: form.surface_max_m2 ? parseFloat(form.surface_max_m2) : null,
        desired_typology: form.desired_typology.trim() || null,
        consent_marketing: form.consent_marketing,
        consent_data_processing: form.consent_data_processing,
      };
      if (isEdit) {
        await axios.put(`${API}/crm/contacts/${existing.contact_id}`, payload, { withCredentials: true });
        toast.success('Contact mis à jour');
      } else {
        await axios.post(`${API}/crm/contacts`, payload, { withCredentials: true });
        toast.success('Contact créé');
      }
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[1500] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => !busy && onClose()} data-testid="crm-contact-form-modal">
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-gradient-to-b from-[#15151A] to-[#0B0B0E] border border-white/10 rounded-2xl shadow-2xl"
      >
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between sticky top-0 bg-[#15151A]/95 backdrop-blur z-10">
          <h2 className="text-lg font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {isEdit ? 'Modifier le contact' : 'Nouveau contact'}
          </h2>
          <button type="button" onClick={onClose} disabled={busy} className="text-white/40 hover:text-white p-1" data-testid="crm-form-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-white/45 font-bold mb-1">Prénom *</label>
              <Input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} required data-testid="crm-form-first-name" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-white/45 font-bold mb-1">Nom</label>
              <Input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} data-testid="crm-form-last-name" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-white/45 font-bold mb-1">Email</label>
              <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="crm-form-email" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-white/45 font-bold mb-1">Téléphone</label>
              <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="crm-form-phone" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-[10px] uppercase tracking-widest text-white/45 font-bold mb-1">Société</label>
              <Input value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} data-testid="crm-form-company" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-white/45 font-bold mb-1">Rôle</label>
              <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} className="w-full bg-white/5 border border-white/15 text-white text-sm rounded-lg h-10 px-3" data-testid="crm-form-role">
                {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-white/45 font-bold mb-1">Statut</label>
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="w-full bg-white/5 border border-white/15 text-white text-sm rounded-lg h-10 px-3" data-testid="crm-form-status">
                {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-widest text-white/45 font-bold mb-1">Tags (séparés par virgule)</label>
            <Input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} placeholder="VIP, Tour Eiffel, Urgent" data-testid="crm-form-tags" />
          </div>

          <div className="border-t border-white/10 pt-4">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#D9A05B] font-bold mb-2">▸ Critères immobiliers</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-white/45 font-bold mb-1">Budget min (€)</label>
                <Input type="number" value={form.budget_min_eur} onChange={e => setForm({ ...form, budget_min_eur: e.target.value })} data-testid="crm-form-budget-min" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-white/45 font-bold mb-1">Budget max (€)</label>
                <Input type="number" value={form.budget_max_eur} onChange={e => setForm({ ...form, budget_max_eur: e.target.value })} data-testid="crm-form-budget-max" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-white/45 font-bold mb-1">Surface min (m²)</label>
                <Input type="number" value={form.surface_min_m2} onChange={e => setForm({ ...form, surface_min_m2: e.target.value })} data-testid="crm-form-surface-min" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-white/45 font-bold mb-1">Surface max (m²)</label>
                <Input type="number" value={form.surface_max_m2} onChange={e => setForm({ ...form, surface_max_m2: e.target.value })} data-testid="crm-form-surface-max" />
              </div>
              <div className="col-span-2">
                <label className="block text-[10px] uppercase tracking-widest text-white/45 font-bold mb-1">Typologie souhaitée</label>
                <Input value={form.desired_typology} onChange={e => setForm({ ...form, desired_typology: e.target.value })} placeholder="T2, T3, Maison…" data-testid="crm-form-typology" />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-widest text-white/45 font-bold mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} className="w-full bg-white/5 border border-white/15 text-white text-sm rounded-lg p-3 placeholder:text-white/30 resize-none" data-testid="crm-form-notes" />
          </div>

          <div className="border-t border-white/10 pt-4">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#D9A05B] font-bold mb-2">▸ RGPD — Consentements</p>
            <label className="flex items-start gap-2 cursor-pointer mb-2">
              <input type="checkbox" checked={form.consent_marketing} onChange={e => setForm({ ...form, consent_marketing: e.target.checked })} className="mt-0.5 accent-[#D9A05B]" data-testid="crm-form-consent-marketing" />
              <span className="text-xs text-white/75">Le contact accepte de recevoir des communications marketing (newsletters, offres promotionnelles).</span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={form.consent_data_processing} onChange={e => setForm({ ...form, consent_data_processing: e.target.checked })} className="mt-0.5 accent-[#D9A05B]" data-testid="crm-form-consent-data" />
              <span className="text-xs text-white/75">Le contact consent au traitement de ses données personnelles dans le cadre du suivi commercial (RGPD Art. 6).</span>
            </label>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-white/10 flex items-center justify-end gap-2 sticky bottom-0 bg-[#0B0B0E]">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button type="submit" disabled={busy || !form.first_name.trim()} className="bg-[#D9A05B] hover:bg-[#C78D46] text-black" data-testid="crm-form-submit-btn">
            <Save className="w-4 h-4 mr-2" />
            {isEdit ? 'Enregistrer' : 'Créer'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CrmContactForm;
